import { useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Heading, ModalControlTriggerProps, Text, TextLink } from '@frontend/design-system';

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${theme.spacing(3)};
    padding: ${theme.spacing(3)};
  `,
  heading: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
    align-items: center;
  `,
  buttonRow: css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacing(2)};
  `,
  infoBanner: css`
    background: ${theme.colors.primary10};
    border-left: 4px solid ${theme.colors.primary50};
    padding: ${theme.spacing(1, 1.5)};
  `,
  linkRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(4)};
  `,
  grayBg: css`
    background: var(--fabric-color-neutral-5);
  `,
};

const URLS = {
  scheduleCall: 'https://www.getweave.com/demo/campaign/xp/payments/',
  videoWalkthrough: 'https://www.weavehelp.com/weavehelp/article/Set-Up-a-Stripe-Account-with-Weave',
  introToPayments: 'https://www.weavehelp.com/weavehelp/article/intro-to-payments',
};

interface SignupPageFooterProps {
  isPaymentsBillingManager: boolean;
  stripeId?: string;
  modalTriggerProps: ModalControlTriggerProps;
  onOnboardClick: ({ stripeId, setLoading }: { stripeId: string; setLoading: (loading: boolean) => void }) => void;
  grayBg?: boolean;
}

export const SignupPageFooter = ({
  isPaymentsBillingManager,
  stripeId,
  modalTriggerProps,
  onOnboardClick,
  grayBg,
}: SignupPageFooterProps) => {
  const { t } = useTranslation('payments');
  const [loading, setLoading] = useState(false);

  const onLinkClick = (link: string) => {
    window.open(link, '_blank');
  };

  return (
    <div css={[styles.container, grayBg && styles.grayBg]} data-testid='signup-page-footer'>
      <div css={styles.heading}>
        <Heading level={2} textAlign='center'>
          {t('Ready to Activate Payments?')}
        </Heading>
        <Text>{t("Your payment processing is already included. Let's get you set up in the next 10 minutes")}</Text>
      </div>
      <span css={styles.buttonRow}>
        <Button
          variant='secondary'
          onClick={() => onLinkClick(URLS.scheduleCall)}
          trackingId='stripe-signup-talk-to-specialist-bottom'
        >
          {t('Talk to a Specialist')}
        </Button>
        <Button
          variant='primary'
          disabled={!isPaymentsBillingManager}
          loading={loading}
          showLabelWhenDisabled
          trackingId='stripe-signup-activate-now-bottom'
          hoverLabel={
            !isPaymentsBillingManager ? t('Must be a Payments Billing Manager to setup Stripe account') : undefined
          }
          {...(!stripeId ? modalTriggerProps : { onClick: () => onOnboardClick({ stripeId, setLoading }) })}
        >
          {!stripeId ? t('Activate Now') : t('Continue Onboarding')}
        </Button>
      </span>
      <span css={styles.infoBanner}>
        <Text>{t('Need help? Our team is standing by to walk you through setup. Chat with us anytime.')}</Text>
      </span>
      <span css={styles.linkRow}>
        <TextLink
          weight='bold'
          onClick={() => onLinkClick(URLS.videoWalkthrough)}
          trackingId='stripe-signup-video-walkthrough'
        >
          {t('Video Walkthrough')}
        </TextLink>
        <TextLink
          weight='bold'
          onClick={() => onLinkClick(URLS.introToPayments)}
          trackingId='stripe-signup-intro-to-payments'
        >
          {t('Intro to Payments')}
        </TextLink>
      </span>
    </div>
  );
};
